import React from "react";
import "../styles/process-visualization.css";
import "../styles/shared-ui.css";
import "../styles/user-stories.css";
import "../styles/prototype.css";
import {
  prototypeErrorMessage,
  saveTwinZip,
  streamPrototype,
} from "../services/prototypeService";

// The build phases, in the order the `status` event reports them.
// "screens" only runs when AI is on.
const PHASES = [
  { id: "stories", label: "User stories" },
  { id: "template", label: "Project" },
  { id: "screens", label: "AI scenes & screens", aiOnly: true },
  { id: "packaging", label: "Packaging" },
];

const asList = (value) => (Array.isArray(value) ? value.filter(Boolean) : []);

const formatElapsed = (seconds) => {
  const m = Math.floor(seconds / 60);
  const s = seconds % 60;
  return m ? `${m}m ${String(s).padStart(2, "0")}s` : `${s}s`;
};

const formatBytes = (bytes) => {
  if (bytes == null) return "";
  if (bytes < 1024) return `${bytes} B`;
  return `${(bytes / 1024).toFixed(1)} KB`;
};

// "✓ AI" or "Built-in" for a screen or conversation of one task.
const SourceTag = ({ ai }) => (
  <span className={`us-badge ${ai ? "pt-src-ai" : "pt-src-builtin"}`}>
    {ai ? "✓ AI" : "Built-in"}
  </span>
);

/**
 * Prototype tab of the Process Studio
 * Builds a digital twin of a process on the backend: a ready-to-run React app
 * (guide, run-a-case, simulation and overview views) delivered as a ZIP. The
 * build streams in, so the page shows each phase, the project files as they
 * are written and the AI's progress per task. Rate-limited to 2 a minute, so
 * it only runs on a button click.
 */
const ProcessPrototypePage = ({ processId: selectedId, onBusyChange }) => {
  const [useAi, setUseAi] = React.useState(true);
  const [storiesAi, setStoriesAi] = React.useState(false);

  const [running, setRunning] = React.useState(false);
  // Options of the run on screen, which may differ from the checkboxes now.
  const [runAi, setRunAi] = React.useState(true);
  const [phase, setPhase] = React.useState(null);
  const [status, setStatus] = React.useState(null);
  // path -> { path, source, content, bytes }, in the order they arrived.
  const [files, setFiles] = React.useState(() => new Map());
  // stepKey -> screen row, and the AI's done / total count.
  const [screens, setScreens] = React.useState(() => new Map());
  const [screenProgress, setScreenProgress] = React.useState(null);
  const [selectedFile, setSelectedFile] = React.useState(null);
  const [result, setResult] = React.useState(null);
  const [error, setError] = React.useState("");
  const [cancelled, setCancelled] = React.useState(false);
  const [elapsed, setElapsed] = React.useState(0);
  const abortRef = React.useRef(null);
  // Only the latest request may update the view if the process changes mid-run.
  const requestSeqRef = React.useRef(0);

  React.useEffect(
    () => () => {
      requestSeqRef.current += 1;
      // Leaving the page closes the stream, which stops the AI on the server.
      abortRef.current?.abort();
    },
    [],
  );

  React.useEffect(() => {
    onBusyChange?.(running);
  }, [running, onBusyChange]);

  // The build can take minutes, so show how long it has been running.
  React.useEffect(() => {
    if (!running) return undefined;
    setElapsed(0);
    const started = Date.now();
    const id = setInterval(
      () => setElapsed(Math.floor((Date.now() - started) / 1000)),
      1000,
    );
    return () => clearInterval(id);
  }, [running]);

  const resetRun = React.useCallback(() => {
    setPhase(null);
    setStatus(null);
    setFiles(new Map());
    setScreens(new Map());
    setScreenProgress(null);
    setSelectedFile(null);
    setResult(null);
    setError("");
    setCancelled(false);
  }, []);

  // The twin belongs to one process; a new one drops it (and any build in flight).
  React.useEffect(() => {
    requestSeqRef.current += 1;
    abortRef.current?.abort();
    abortRef.current = null;
    setRunning(false);
    resetRun();
  }, [selectedId, resetRun]);

  const handleGenerate = async () => {
    if (!selectedId || running) return;
    const seq = ++requestSeqRef.current;
    const live = () => seq === requestSeqRef.current;
    const controller = new AbortController();
    abortRef.current = controller;
    let finished = false;

    resetRun();
    setRunAi(useAi);
    setRunning(true);
    setStatus("Starting…");

    try {
      await streamPrototype(
        selectedId,
        { ai: useAi, storiesAi, signal: controller.signal },
        {
          status: (data) => {
            if (!live()) return;
            setPhase(data?.phase || null);
            setStatus(data?.message || null);
          },
          file: (data) => {
            if (!live() || !data?.path) return;
            setFiles((prev) => new Map(prev).set(data.path, data));
            // Preview the first file until the user picks one.
            setSelectedFile((cur) => cur ?? data.path);
          },
          screen: (data) => {
            if (!live() || !data?.stepKey) return;
            setScreens((prev) => new Map(prev).set(data.stepKey, data));
            if (data.total) {
              setScreenProgress({ done: data.done ?? 0, total: data.total });
            }
          },
          done: (data) => {
            finished = true;
            if (!live()) return;
            setResult(data);
            setPhase(null);
            try {
              saveTwinZip(data);
            } catch (e) {
              console.error("Failed to save the digital twin ZIP:", e);
              setError("The twin was built but the ZIP could not be saved. Use Download ZIP to try again.");
            }
          },
          error: (data) => {
            finished = true;
            if (!live()) return;
            setError(data?.message || "Something went wrong. Please try again.");
          },
        },
      );
      if (!finished && live()) {
        setError(
          "The connection closed before the digital twin was finished. Please try again.",
        );
      }
    } catch (e) {
      if (!live()) return;
      if (e?.name === "AbortError") {
        setCancelled(true);
      } else {
        console.error("Failed to build the digital twin:", e);
        setError(prototypeErrorMessage(e));
      }
    } finally {
      if (live()) {
        setRunning(false);
        setStatus(null);
        abortRef.current = null;
      }
    }
  };

  const handleCancel = () => abortRef.current?.abort();

  const handleDownload = () => {
    try {
      saveTwinZip(result);
      setError("");
    } catch (e) {
      console.error("Failed to save the digital twin ZIP:", e);
      setError("The ZIP could not be saved. Please try again.");
    }
  };

  // After `done`, its lists are the final word; file contents only come live.
  const fileRows = React.useMemo(() => {
    const doneFiles = asList(result?.files);
    if (!doneFiles.length) return Array.from(files.values());
    return doneFiles.map((f) => ({ ...files.get(f.path), ...f }));
  }, [files, result]);
  const screenRows = result ? asList(result.screens) : Array.from(screens.values());
  const previewFile = selectedFile ? files.get(selectedFile) : null;
  const generation = result?.generation || {};
  const aiFileCount = fileRows.filter((f) => f.source === "AI").length;
  const phases = PHASES.filter((p) => runAi || !p.aiOnly);
  const phaseIndex = phases.findIndex((p) => p.id === phase);
  const hasOutput = running || result || fileRows.length > 0;

  return (
    <div className="ps-tab-body">
      <div className="role-card">
        <div className="section-title">Create Digital Twin</div>
        <div className="form-grid">
          <div className="form-group pt-options" style={{ alignSelf: "end" }}>
            <label
              className="us-ai-toggle"
              title="AI writes each task's conversation, documents and work screen (about 10-40 s per task). Without AI, built-in content is used and the twin is ready in seconds."
            >
              <input
                type="checkbox"
                checked={useAi}
                onChange={(e) => setUseAi(e.target.checked)}
                disabled={running}
              />
              AI scenes and screens
            </label>
            <label
              className="us-ai-toggle"
              title="Write the user-story text with AI before building the twin. Adds about 2-3 minutes; otherwise rule-based stories are used."
            >
              <input
                type="checkbox"
                checked={storiesAi}
                onChange={(e) => setStoriesAi(e.target.checked)}
                disabled={running}
              />
              AI user stories (+2–3 min)
            </label>
          </div>
          <div className="form-group us-actions" style={{ alignSelf: "end" }}>
            <button
              className="primary-btn"
              onClick={handleGenerate}
              disabled={!selectedId || running}
            >
              {running ? "Creating…" : "Create Digital Twin"}
            </button>
            {running && (
              <button
                type="button"
                className="secondary-btn"
                onClick={handleCancel}
                title="Stop building; this also stops the AI on the server"
              >
                Cancel
              </button>
            )}
          </div>
        </div>
      </div>

      {error && (
        <div role="alert" className="us-alert us-alert-error pt-error">
          <span>⚠ {error}</span>
          {!running && selectedId && (
            <button type="button" className="secondary-btn" onClick={handleGenerate}>
              Try again
            </button>
          )}
        </div>
      )}

      {cancelled && (
        <div role="status" className="us-alert us-alert-info">
          Cancelled. The AI on the server was stopped.
        </div>
      )}

      {result && generation.aiError && (
        <div role="alert" className="us-alert us-alert-warning">
          ⚠ Built-in content was used where AI could not be: {generation.aiError}
        </div>
      )}

      {hasOutput && (
        <div className="role-card pt-progress">
          <ol className="pt-phases">
            {phases.map((p, i) => {
              const state =
                result || (phaseIndex >= 0 && i < phaseIndex) ? "done"
                : i === phaseIndex && running ? "active"
                : "pending";
              return (
                <li key={p.id} className={`pt-phase pt-phase-${state}`}>
                  <span className="pt-phase-dot">
                    {state === "done" ? "✓" : i + 1}
                  </span>
                  {p.label}
                </li>
              );
            })}
          </ol>

          {running && (
            <div className="us-progress-line">
              <span className="us-spinner" aria-hidden="true" />
              <span className="us-progress-text" role="status">
                {status || "Working…"}
              </span>
              <span className="us-muted">{formatElapsed(elapsed)}</span>
            </div>
          )}

          {result && (
            <div className="pt-summary">
              <div>
              <strong>{result.fileName}</strong> is ready and has been
              downloaded. {fileRows.length} files
              {aiFileCount > 0 && `, ${aiFileCount} written by AI`}.
              {generation.aiUsed && generation.model && (
                <span className="us-muted"> AI model: {generation.model}.</span>
              )}
              {generation.storiesTextSource && (
                <span className="us-muted">
                  {" "}User stories: {generation.storiesTextSource === "AI" ? "AI" : "rule-based"} text.
                </span>
              )}
              </div>
              <button
                type="button"
                className="primary-btn"
                onClick={handleDownload}
                style={{ fontWeight: 600 }}
              >
                Download ZIP
              </button>
            </div>
          )}
        </div>
      )}

      {screenRows.length > 0 || (running && runAi && screenProgress) ?
        <div className="role-card" style={{ marginTop: 12 }}>
          <div className="section-title">AI work per task</div>
          {screenProgress && !result && (
            <>
              <div
                className="us-progress-bar"
                role="progressbar"
                aria-valuemin={0}
                aria-valuemax={screenProgress.total}
                aria-valuenow={screenProgress.done}
              >
                <span
                  style={{
                    width: `${Math.min(
                      100,
                      (screenProgress.done / screenProgress.total) * 100,
                    )}%`,
                  }}
                />
              </div>
              <div className="us-muted" style={{ margin: "6px 0 10px" }}>
                {screenProgress.done} of {screenProgress.total} tasks finished
              </div>
            </>
          )}
          <div className="us-table-wrap">
            <table className="us-table">
              <thead>
                <tr>
                  <th>Task</th>
                  <th>Work screen</th>
                  <th>Conversation &amp; documents</th>
                  <th>Note</th>
                </tr>
              </thead>
              <tbody>
                {screenRows.map((row) => (
                  <tr key={row.stepKey}>
                    <td>{row.title || row.stepKey}</td>
                    <td>
                      <SourceTag ai={row.source === "AI"} />
                    </td>
                    <td>
                      <SourceTag ai={row.sceneSource === "AI"} />
                    </td>
                    <td className="us-muted">{row.error || ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      : null}

      {fileRows.length > 0 && (
        <div className="role-card" style={{ marginTop: 12 }}>
          <div className="section-title">
            Project files ({fileRows.length})
          </div>
          <div className="pt-files">
            <ul className="pt-file-list">
              {fileRows.map((f) => (
                <li key={f.path}>
                  <button
                    type="button"
                    className={`pt-file${f.path === selectedFile ? " active" : ""}`}
                    onClick={() => setSelectedFile(f.path)}
                    disabled={f.content == null}
                    title={f.content == null ? "No preview for this file" : f.path}
                  >
                    <span className="pt-file-path">{f.path}</span>
                    {f.source === "AI" && <span className="us-badge us-ai">AI</span>}
                    {f.bytes != null && (
                      <span className="pt-file-size">{formatBytes(f.bytes)}</span>
                    )}
                  </button>
                </li>
              ))}
            </ul>
            <div className="pt-preview">
              {previewFile ?
                <>
                  <div className="pt-preview-head">{previewFile.path}</div>
                  <pre>{previewFile.content}</pre>
                </>
              : <div className="us-muted pt-preview-empty">
                  Select a file to preview it.
                </div>
              }
            </div>
          </div>
        </div>
      )}

      {result && (
        <div className="role-card" style={{ marginTop: 12 }}>
          <div className="section-title">How to run the twin</div>
          <ol className="pt-run-steps">
            <li>
              Unzip <code>{result.fileName}</code>. It contains the folder{" "}
              <code>{result.folder || "<code>-digital-twin"}</code>.
            </li>
            <li>
              In that folder run <code>npm install</code>, then{" "}
              <code>npm run dev</code>, and open the address it prints.
            </li>
          </ol>
          <ul className="us-list us-muted pt-run-notes">
            <li>Node.js 18 or newer is needed. There is no backend: all data is in <code>src/data/twin.json</code>.</li>
            <li>If npm blocks esbuild&apos;s install script with an allow-scripts warning, run <code>npm approve-scripts esbuild</code>.</li>
            <li>Conversations are read aloud with the browser&apos;s voices (Chrome and Edge work best). Documents download as .doc files for Word.</li>
          </ul>
        </div>
      )}
    </div>
  );
};

export default ProcessPrototypePage;
